import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import {
    fetchCustomerSubscriptions,
    fetchOrganizationAppointments,
    fetchOrganizationContacts,
    fetchOrganizationCustomerAddresses,
    fetchOrganizationCustomerAppliances,
    fetchOrganizationCustomerData,
    fetchOrganizationCustomerEnables,
    fetchOrganizationCustomerHistory,
    fetchOrganizationCustomerLandingpages,
    fetchOrganizationCustomerOrders,
    fetchOrganizationCustomerReferralPartner,
    fetchOrganizationCustomers,
    fetchOrganizationFavorites,
    fetchOrganizationLeadUnlock,
    fetchOrganizationLicensors,
    fetchOrganizationOffers,
    fetchOrganizationOfficeProfis,
    fetchOrganizationOrders,
    fetchOrganizationOverview,
    fetchOrganizationPlacements,
    fetchOrganizationRanking,
    fetchOrganizationSisPartners,
    fetchOrganizationStatistic,
    fetchOrganizationTodos,
    fetchRegisteredEvents,
    fetchVideoList,
} from 'src/api/organization';
import {
    FETCH_ORGANIZATION_APPOINTMENTS,
    FETCH_ORGANIZATION_CONTACTS,
    FETCH_ORGANIZATION_CUSTOMERS,
    FETCH_ORGANIZATION_CUSTOMER_ADDRESSES,
    FETCH_ORGANIZATION_CUSTOMER_APPLIANCES,
    FETCH_ORGANIZATION_CUSTOMER_DATA,
    FETCH_ORGANIZATION_CUSTOMER_ENABLES,
    FETCH_ORGANIZATION_CUSTOMER_HISTORY,
    FETCH_ORGANIZATION_CUSTOMER_LANDINGPAGES,
    FETCH_ORGANIZATION_CUSTOMER_ORDERS,
    FETCH_ORGANIZATION_CUSTOMER_REFERRAL_PARTNER,
    FETCH_ORGANIZATION_CUSTOMER_SUBSCRIPTIONS,
    FETCH_ORGANIZATION_FAVORITES,
    FETCH_ORGANIZATION_LEAD_UNLOCK,
    FETCH_ORGANIZATION_LICENSORS,
    FETCH_ORGANIZATION_LIST_COUNTS,
    FETCH_ORGANIZATION_OFFERS,
    FETCH_ORGANIZATION_OFFICE_PROFIS,
    FETCH_ORGANIZATION_ORDERS,
    FETCH_ORGANIZATION_OVERVIEW,
    FETCH_ORGANIZATION_PLACEMENTS,
    FETCH_ORGANIZATION_RANKING,
    FETCH_ORGANIZATION_SIS_PARTNERS,
    FETCH_ORGANIZATION_STATISTIC,
    FETCH_ORGANIZATION_TODOS,
    FETCH_ORGANIZATION_TODO_COUNT,
    FETCH_REGISTERED_EVENTS,
    FETCH_VIDEO_LIST,
    requestOrganizationAppointments,
    requestOrganizationAppointmentsFailure,
    requestOrganizationAppointmentsSuccess,
    requestOrganizationContacts,
    requestOrganizationContactsFailure,
    requestOrganizationContactsSuccess,
    requestOrganizationCustomerAddresses,
    requestOrganizationCustomerAddressesFailure,
    requestOrganizationCustomerAddressesSuccess,
    requestOrganizationCustomerAppliances,
    requestOrganizationCustomerAppliancesFailure,
    requestOrganizationCustomerAppliancesSuccess,
    requestOrganizationCustomerData,
    requestOrganizationCustomerDataFailure,
    requestOrganizationCustomerDataSuccess,
    requestOrganizationCustomerEnables,
    requestOrganizationCustomerEnablesFailure,
    requestOrganizationCustomerEnablesSuccess,
    requestOrganizationCustomerHistory,
    requestOrganizationCustomerHistoryFailure,
    requestOrganizationCustomerHistorySuccess,
    requestOrganizationCustomerLandingpages,
    requestOrganizationCustomerLandingpagesFailure,
    requestOrganizationCustomerLandingpagesSuccess,
    requestOrganizationCustomerOrders,
    requestOrganizationCustomerOrdersFailure,
    requestOrganizationCustomerOrdersSuccess,
    requestOrganizationCustomerReferralPartner,
    requestOrganizationCustomerReferralPartnerFailure,
    requestOrganizationCustomerReferralPartnerSuccess,
    requestOrganizationCustomers,
    requestOrganizationCustomersFailure,
    requestOrganizationCustomersSuccess,
    requestOrganizationCustomerSubscriptions,
    requestOrganizationCustomerSubscriptionsFailure,
    requestOrganizationCustomerSubscriptionsSuccess,
    requestOrganizationFavorites,
    requestOrganizationFavoritesFailure,
    requestOrganizationFavoritesSuccess,
    requestOrganizationLeadUnlock,
    requestOrganizationLeadUnlockFailure,
    requestOrganizationLeadUnlockSuccess,
    requestOrganizationLicensors,
    requestOrganizationLicensorsFailure,
    requestOrganizationLicensorsSuccess,
    requestOrganizationListCounts,
    requestOrganizationListCountsFailure,
    requestOrganizationListCountsSuccess,
    requestOrganizationOffers,
    requestOrganizationOffersFailure,
    requestOrganizationOffersSuccess,
    requestOrganizationOfficeProfis,
    requestOrganizationOfficeProfisFailure,
    requestOrganizationOfficeProfisSuccess,
    requestOrganizationOrders,
    requestOrganizationOrdersFailure,
    requestOrganizationOrdersSuccess,
    requestOrganizationOverview,
    requestOrganizationOverviewFailure,
    requestOrganizationOverviewSuccess,
    requestOrganizationPlacements,
    requestOrganizationPlacementsFailure,
    requestOrganizationPlacementsSuccess,
    requestOrganizationRanking,
    requestOrganizationRankingFailure,
    requestOrganizationRankingSuccess,
    requestOrganizationSisPartners,
    requestOrganizationSisPartnersFailure,
    requestOrganizationSisPartnersSuccess,
    requestOrganizationStatistic,
    requestOrganizationStatisticFailure,
    requestOrganizationStatisticSuccess,
    requestOrganizationTodoCount,
    requestOrganizationTodoCountFailure,
    requestOrganizationTodoCountSuccess,
    requestOrganizationTodos,
    requestOrganizationTodosFailure,
    requestOrganizationTodosSuccess,
    requestRegisteredEvents,
    requestRegisteredEventsFailure,
    requestRegisteredEventsSuccess,
    requestVideoList,
    requestVideoListFailure,
    requestVideoListSuccess,
} from './actions';

/* Organization statsitic */
function* fetchOrganizationStatisticSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationStatistic());

    const { error, response } = yield call(fetchOrganizationStatistic, action.id, action.range);

    if (error) yield put(requestOrganizationStatisticFailure(error));
    else yield put(requestOrganizationStatisticSuccess(response));
}

export function* generateFetchOrganizationStatisticWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_STATISTIC, fetchOrganizationStatisticSaga);
}

/* Organization lead-unlock */
function* fetchOrganizationLeadUnlockSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationLeadUnlock());

    const { error, response } = yield call(fetchOrganizationLeadUnlock, action.id);

    if (error) yield put(requestOrganizationLeadUnlockFailure(error));
    else yield put(requestOrganizationLeadUnlockSuccess(response));
}

export function* generateFetchOrganizationLeadUnlockWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_LEAD_UNLOCK, fetchOrganizationLeadUnlockSaga);
}

/* Organization ranking */
function* fetchOrganizationRankingSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationRanking());

    const { error, response } = yield call(fetchOrganizationRanking, action.id);

    if (error) yield put(requestOrganizationRankingFailure(error));
    else yield put(requestOrganizationRankingSuccess(response));
}

export function* generateFetchOrganizationRankingWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_RANKING, fetchOrganizationRankingSaga);
}

/* Organization appointments */
function* fetchOrganizationAppointmentsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationAppointments());

    const { error, response } = yield call(
        fetchOrganizationAppointments,
        action.id,
        action.timeframe,
        action.typeFilter,
        action.page,
        action.pageSize,
        action.sortBy,
        action.sortDirection
    );

    if (error) yield put(requestOrganizationAppointmentsFailure(error));
    else yield put(requestOrganizationAppointmentsSuccess(response));
}

export function* generateFetchOrganizationAppointmentsWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_APPOINTMENTS, fetchOrganizationAppointmentsSaga);
}

/* Organisation-wide orders */
function* fetchOrganizationOrdersSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationOrders());

    const { error, response } = yield call(fetchOrganizationOrders, action.id, action.params);

    if (error) yield put(requestOrganizationOrdersFailure(error));
    else yield put(requestOrganizationOrdersSuccess(response));
}

export function* generateFetchOrganizationOrdersWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_ORDERS, fetchOrganizationOrdersSaga);
}

/* Organisation-scoped offers */
function* fetchOrganizationOffersSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationOffers());

    const { error, response } = yield call(fetchOrganizationOffers, action.id, action.params);

    if (error) yield put(requestOrganizationOffersFailure(error));
    else yield put(requestOrganizationOffersSuccess(response));
}

export function* generateFetchOrganizationOffersWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_OFFERS, fetchOrganizationOffersSaga);
}

/* Organization Overview */
function* fetchOrganizationOverviewSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationOverview());

    const { error, response } = yield call(fetchOrganizationOverview, action.id, action.pageable);

    if (error) yield put(requestOrganizationOverviewFailure(error));
    else yield put(requestOrganizationOverviewSuccess(response));
}

export function* generateFetchOrganizationOverviewWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_OVERVIEW, fetchOrganizationOverviewSaga);
}

/* Organization contacts */
function* fetchOrganizationContactsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationContacts());

    const { error, response } = yield call(fetchOrganizationContacts, action.id, action.pageable, action.filterParams);

    if (error) yield put(requestOrganizationContactsFailure(error));
    else yield put(requestOrganizationContactsSuccess(response));
}

export function* generateFetchOrganizationContactsWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CONTACTS, fetchOrganizationContactsSaga);
}

/* Organization to-dos */
function* fetchOrganizationTodosSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationTodos());

    const { error, response } = yield call(fetchOrganizationTodos, action.id, action.pageable, action.category);

    if (error) yield put(requestOrganizationTodosFailure(error));
    else yield put(requestOrganizationTodosSuccess(response));
}

export function* generateFetchOrganizationTodosWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_TODOS, fetchOrganizationTodosSaga);
}

/* Organization to-do count (total across all categories, one item fetched just for the total) */
function* fetchOrganizationTodoCountSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationTodoCount());

    const { error, response } = yield call(fetchOrganizationTodos, action.id, { page: 0, pageSize: 1 }, 'all');

    if (error) yield put(requestOrganizationTodoCountFailure(error));
    else yield put(requestOrganizationTodoCountSuccess(response));
}

export function* generateFetchOrganizationTodoCountWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_TODO_COUNT, fetchOrganizationTodoCountSaga);
}

/* Organization list-view counts — one pageSize=1 probe per list, unfiltered, so each badge is a grand total */
function* fetchOrganizationListCountsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationListCounts());

    const [contacts, appointments, orders, offers] = yield all([
        call(fetchOrganizationContacts, action.id, { page: 0, pageSize: 1 }, {}),
        call(fetchOrganizationAppointments, action.id, 'all', 'all', 0, 1),
        call(fetchOrganizationOrders, action.id, { page: 0, pageSize: 1 }),
        call(fetchOrganizationOffers, action.id, { page: 0, pageSize: 1 }),
    ]);

    const results = [contacts, appointments, orders, offers];
    if (results.every(r => r.error)) {
        yield put(requestOrganizationListCountsFailure(contacts.error));
        return;
    }

    const totalOf = (r: any) => r.response?.data?.totalElements ?? 0;
    yield put(
        requestOrganizationListCountsSuccess({
            contacts: totalOf(contacts),
            appointments: totalOf(appointments),
            orders: totalOf(orders),
            offers: totalOf(offers),
        })
    );
}

export function* generateFetchOrganizationListCountsWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_LIST_COUNTS, fetchOrganizationListCountsSaga);
}

/* Fetch Registered Events */
function* fetchRegisteredEventsSaga(action: AnyAction): SagaIterator {
    yield put(requestRegisteredEvents());

    const { error, response } = yield call(fetchRegisteredEvents, action.id, action.partnerId);

    if (error) yield put(requestRegisteredEventsFailure(error));
    else yield put(requestRegisteredEventsSuccess(response));
}

export function* generatefetchRegisteredEventsWatcher() {
    yield takeLatest(FETCH_REGISTERED_EVENTS, fetchRegisteredEventsSaga);
}

/* Fetch Organization Customer Appliances */
function* fetchOrganizationCustomerAppliancesSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerAppliances());

    const { error, response } = yield call(fetchOrganizationCustomerAppliances, action.sourceId, action.targetId);

    if (error) yield put(requestOrganizationCustomerAppliancesFailure(error));
    else yield put(requestOrganizationCustomerAppliancesSuccess(response));
}

export function* generateFetchOrganizationCustomerAppliancesWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_APPLIANCES, fetchOrganizationCustomerAppliancesSaga);
}

/* Fetch Video List */
function* fetchVideoListSaga(action: AnyAction): SagaIterator {
    yield put(requestVideoList());

    const { error, response } = yield call(fetchVideoList, action.id, action.partnerId);

    if (error) yield put(requestVideoListFailure(error));
    else yield put(requestVideoListSuccess(response));
}

export function* generatefetchVideoListWatcher() {
    yield takeLatest(FETCH_VIDEO_LIST, fetchVideoListSaga);
}

/* Fetch Organization Customer Orders */
function* fetchOrganizationCustomerOrdersSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerOrders());

    const { error, response } = yield call(
        fetchOrganizationCustomerOrders,
        action.id,
        action.partnerId,
        action.pageable
    );

    if (error) yield put(requestOrganizationCustomerOrdersFailure(error));
    else yield put(requestOrganizationCustomerOrdersSuccess(response));
}

export function* generateFetchOrganizationCustomerOrdersWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_ORDERS, fetchOrganizationCustomerOrdersSaga);
}

/* Fetch Organization Customer Subscriptions */
function* fetchOrganizationCustomerSubscriptionsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerSubscriptions());

    const { error, response } = yield call(fetchCustomerSubscriptions, action.id, action.customerId, action.pageable);

    if (error) yield put(requestOrganizationCustomerSubscriptionsFailure(error));
    else yield put(requestOrganizationCustomerSubscriptionsSuccess(response));
}

export function* generateFetchOrganizationCustomerSubscriptionsWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_SUBSCRIPTIONS, fetchOrganizationCustomerSubscriptionsSaga);
}

/* Fetch Organization Customer Data */
function* fetchOrganizationCustomerDataSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerData());

    const { error, response } = yield call(fetchOrganizationCustomerData, action.sourceId, action.targetId);

    if (error) yield put(requestOrganizationCustomerDataFailure(error));
    else yield put(requestOrganizationCustomerDataSuccess(response));
}

export function* generateFetchOrganizationCustomerDataWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_DATA, fetchOrganizationCustomerDataSaga);
}

/* Fetch Organization Customer Addresses */
function* fetchOrganizationCustomerAddressesSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerAddresses());

    const { error, response } = yield call(fetchOrganizationCustomerAddresses, action.sourceId, action.targetId);

    if (error) yield put(requestOrganizationCustomerAddressesFailure(error));
    else yield put(requestOrganizationCustomerAddressesSuccess(response));
}

export function* generateFetchOrganizationCustomerAddressesWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_ADDRESSES, fetchOrganizationCustomerAddressesSaga);
}

/* Fetch Organization Customer Landingpages */
function* fetchOrganizationCustomerLandingpagesSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerLandingpages());

    const { error, response } = yield call(fetchOrganizationCustomerLandingpages, action.sourceId, action.targetId);

    if (error) yield put(requestOrganizationCustomerLandingpagesFailure(error));
    else yield put(requestOrganizationCustomerLandingpagesSuccess(response));
}

export function* generateFetchOrganizationCustomerLandingpagesWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_LANDINGPAGES, fetchOrganizationCustomerLandingpagesSaga);
}

/* Fetch Organization Customer Referral Partner */
function* fetchOrganizationCustomerReferralPartnerSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerReferralPartner());

    const { error, response } = yield call(fetchOrganizationCustomerReferralPartner, action.sourceId, action.targetId);

    if (error) yield put(requestOrganizationCustomerReferralPartnerFailure(error));
    else yield put(requestOrganizationCustomerReferralPartnerSuccess(response));
}

export function* generateFetchOrganizationCustomerReferralPartnerWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_REFERRAL_PARTNER, fetchOrganizationCustomerReferralPartnerSaga);
}

/* Fetch Organization Customer Enables */
function* fetchOrganizationCustomerEnablesSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerEnables());

    const { error, response } = yield call(fetchOrganizationCustomerEnables, action.sourceId, action.targetId);

    if (error) yield put(requestOrganizationCustomerEnablesFailure(error));
    else yield put(requestOrganizationCustomerEnablesSuccess(response));
}

export function* generateFetchOrganizationCustomerEnablesWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_ENABLES, fetchOrganizationCustomerEnablesSaga);
}

/* Fetch Favorites */
function* fetchOrganizationFavoritesSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationFavorites());

    const { error, response } = yield call(fetchOrganizationFavorites, action.id);

    if (error) yield put(requestOrganizationFavoritesFailure(error));
    else yield put(requestOrganizationFavoritesSuccess(response));
}

export function* generateFetchOrganizationFavoritesWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_FAVORITES, fetchOrganizationFavoritesSaga);
}

/* Fetch Organization Customers */
function* fetchOrganizationCustomersSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomers());

    const { error, response } = yield call(fetchOrganizationCustomers, action.id, action.pageable);

    if (error) yield put(requestOrganizationCustomersFailure(error));
    else yield put(requestOrganizationCustomersSuccess(response));
}

export function* generateFetchOrganizationCustomersWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMERS, fetchOrganizationCustomersSaga);
}

/* Fetch Organization Sis Partners */
function* fetchOrganizationSisPartnersSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationSisPartners());

    const { error, response } = yield call(fetchOrganizationSisPartners, action.id, action.pageable);

    if (error) yield put(requestOrganizationSisPartnersFailure(error));
    else yield put(requestOrganizationSisPartnersSuccess(response));
}

export function* generateFetchOrganizationSisPartnersWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_SIS_PARTNERS, fetchOrganizationSisPartnersSaga);
}

/* Fetch Organization Licensors */
function* fetchOrganizationLicensorsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationLicensors());

    const { error, response } = yield call(fetchOrganizationLicensors, action.id, action.pageable);

    if (error) yield put(requestOrganizationLicensorsFailure(error));
    else yield put(requestOrganizationLicensorsSuccess(response));
}

export function* generateFetchOrganizationLicensorsWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_LICENSORS, fetchOrganizationLicensorsSaga);
}

/* Fetch Organization Placements */
function* fetchOrganizationPlacementsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationPlacements());

    const { error, response } = yield call(fetchOrganizationPlacements, action.id, action.pageable);

    if (error) yield put(requestOrganizationPlacementsFailure(error));
    else yield put(requestOrganizationPlacementsSuccess(response));
}

export function* generateFetchOrganizationPlacementsWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_PLACEMENTS, fetchOrganizationPlacementsSaga);
}

/* Fetch Organization Office Profis */
function* fetchOrganizationOfficeProfisSaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationOfficeProfis());

    const { error, response } = yield call(fetchOrganizationOfficeProfis, action.id, action.pageable);

    if (error) yield put(requestOrganizationOfficeProfisFailure(error));
    else yield put(requestOrganizationOfficeProfisSuccess(response));
}

export function* generateFetchOrganizationOfficeProfisWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_OFFICE_PROFIS, fetchOrganizationOfficeProfisSaga);
}

/* Fetch Organization Customer History */
function* fetchOrganizationCustomerHistorySaga(action: AnyAction): SagaIterator {
    yield put(requestOrganizationCustomerHistory());

    const { error, response } = yield call(
        fetchOrganizationCustomerHistory,
        action.id,
        action.customerId,
        action.pageable
    );

    if (error) yield put(requestOrganizationCustomerHistoryFailure(error));
    else yield put(requestOrganizationCustomerHistorySuccess(response));
}

export function* generateFetchOrganizationCustomerHistoryWatcher() {
    yield takeLatest(FETCH_ORGANIZATION_CUSTOMER_HISTORY, fetchOrganizationCustomerHistorySaga);
}
